import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import type { User } from '@supabase/supabase-js'
import {
  subscribeAuth,
  login as authLogin,
  register as authRegister,
  signOut as authSignOut,
  type RegisterOptions,
} from '../services/auth'
import { recordPlatformLoginEventOnce } from '../features/superadmin/security/loginHistory.service'
import {
  assertPlatformAccountAccess,
  PlatformAccountBlockedError,
  storePlatformAccountBlockedMessage,
} from '../services/platformAccountAccess'
import { usePlatformAccountGuard } from '../hooks/usePlatformAccountGuard'
import { supabase } from '../services/supabase'
import {
  getProfile,
  createInitialAppProfile,
  ensureAppProfileExists,
  promoteAppProfileSalonOwner,
  type AppProfile,
  type SalonMemberRole,
} from '../services/profile'
import { getDisplayNameFromUserMetadata, getPhoneFromUserMetadata } from '../lib/appProfileFromUserMetadata'

export interface RegisterOutcome {
  /** True als Supabase geen sessie teruggeeft tot de gebruiker de bevestigingsmail volgt. */
  needsEmailConfirmation: boolean
  /** True bij Supabase anti-enumeratie: adres is waarschijnlijk al bevestigd geregistreerd. */
  likelyAlreadyRegistered: boolean
}

interface AuthContextValue {
  user: User | null
  profile: AppProfile | null
  /** True tot auth-sessie bekend is (user of null). */
  loading: boolean
  /** True zolang loadProfile voor de huidige user loopt. Los van profile === null. */
  profileLoading: boolean
  login: (email: string, wachtwoord: string) => Promise<void>
  register: (email: string, wachtwoord: string, options?: RegisterOptions) => Promise<RegisterOutcome>
  logout: () => Promise<void>
  /** Vernieuw het profiel uit de database (na bv. salon aanmaken). */
  refreshProfile: () => Promise<void>
  isSuperadmin: boolean
  isAdmin: boolean
  isUser: boolean
  /** True alleen als pure klant (geen salon-team). */
  isKlant: boolean
  /** Salon-IDs met volledige admin-rechten (`salon_members.role = admin`). */
  adminSalonIds: string[]
  /** Alle salons waar gebruiker teamlid is (admin, manager, employee). */
  staffSalonIds: string[]
  /** Rol per salon in `salon_members`. */
  salonMemberRoles: Partial<Record<string, SalonMemberRole>>
  /** Volledig beheer voor deze salon (instellingen, uitnodigen, betaling). */
  isFullSalonAdmin: (salonId: string) => boolean
  /** Teamtoegang inclusief employee/manager. */
  isSalonStaffMember: (salonId: string) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Stabiele referentie zodat context niet bij elke render verandert wanneer profile null is. */
const EMPTY_SALON_IDS: string[] = []

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<AppProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const profileLoadGenRef = useRef(0)

  const loadProfile = useCallback(async (uid: string, options?: { recordLogin?: boolean }) => {
    const gen = ++profileLoadGenRef.current
    setProfileLoading(true)
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (authUser?.id !== uid) {
        const p = await getProfile(uid)
        if (gen !== profileLoadGenRef.current) return
        setProfile(p)
        return
      }
      /** Eerst koppelen: alle salons via e-mail (`p_salon_id` null), idempotente RPC. */
      if (authUser.id === uid) {
        const { error: rpcErr } = await supabase.rpc('mark_medewerker_invite_accepted')
        if (rpcErr && import.meta.env.DEV) console.debug('[mark_medewerker_invite_accepted]', rpcErr.message)
        const { error: linkErr } = await supabase.rpc('link_klant_auth_by_email', {
          p_salon_id: null,
        })
        if (linkErr && import.meta.env.DEV) {
          console.debug('[link_klant_auth_by_email]', linkErr.message)
        }
        await ensureAppProfileExists(authUser)
      }
      const accountKind = (authUser.user_metadata as { account_kind?: string }).account_kind
      let p = await getProfile(uid)
      if (
        accountKind === 'salon_owner' &&
        p?.role === 'user' &&
        (p?.staffSalonIds?.length ?? 0) === 0
      ) {
        await promoteAppProfileSalonOwner()
        p = await getProfile(uid)
      }
      if (gen !== profileLoadGenRef.current) return
      setProfile(p)
      await assertPlatformAccountAccess()
      if (options?.recordLogin) {
        const { data: { session } } = await supabase.auth.getSession()
        void recordPlatformLoginEventOnce('login_success', {
          userId: uid,
          accessToken: session?.access_token ?? null,
        }).catch((error) => {
          if (import.meta.env.DEV) {
            console.debug('[recordPlatformLoginEventOnce]', error)
          }
        })
      }
    } catch (e) {
      if (gen !== profileLoadGenRef.current) return
      if (e instanceof PlatformAccountBlockedError) {
        storePlatformAccountBlockedMessage(e.message)
        setProfile(null)
        setUser(null)
        throw e
      }
      setProfile(null)
    } finally {
      if (gen === profileLoadGenRef.current) {
        setProfileLoading(false)
      }
    }
  }, [])

  const handlePlatformAccountBlocked = useCallback((error: PlatformAccountBlockedError) => {
    storePlatformAccountBlockedMessage(error.message)
    profileLoadGenRef.current += 1
    setUser(null)
    setProfile(null)
    setProfileLoading(false)
  }, [])

  usePlatformAccountGuard({
    userId: user?.id,
    onBlocked: handlePlatformAccountBlocked,
  })

  useEffect(() => {
    const unsub = subscribeAuth((u, event) => {
      setUser(u)
      if (u) {
        setProfile(null)
        void loadProfile(u.id, { recordLogin: event === 'SIGNED_IN' }).catch((e) => {
          if (e instanceof PlatformAccountBlockedError) {
            setUser(null)
            setProfile(null)
          }
        })
      } else {
        profileLoadGenRef.current += 1
        setProfile(null)
        setProfileLoading(false)
      }
      setLoading(false)
    })
    return unsub
  }, [loadProfile])

  const login = useCallback(async (email: string, wachtwoord: string) => {
    const data = await authLogin(email, wachtwoord)
    if (data.user) await loadProfile(data.user.id)
  }, [loadProfile])

  const register = useCallback(async (email: string, wachtwoord: string, options?: RegisterOptions) => {
    const result = await authRegister(email, wachtwoord, options)
    const accountKind = options?.accountKind ?? 'klant'
    const initialRole = accountKind === 'salon_owner' ? 'salon' : 'user'
    if (result.user && result.session) {
      const meta = (result.user.user_metadata ?? {}) as Record<string, unknown>
      try {
        await createInitialAppProfile(result.user.id, initialRole, {
          displayName: getDisplayNameFromUserMetadata(result.user) ?? undefined,
          phone: getPhoneFromUserMetadata(meta) ?? undefined,
        })
      } catch {
        // loadProfile roept ensureAppProfileExists aan als rij ontbreekt
      }
      await loadProfile(result.user.id)
    }
    return {
      needsEmailConfirmation: result.needsEmailConfirmation,
      likelyAlreadyRegistered: result.likelyAlreadyRegistered,
    }
  }, [loadProfile])

  const logout = useCallback(async () => {
    try {
      await authSignOut()
    } finally {
      profileLoadGenRef.current += 1
      setProfile(null)
      setProfileLoading(false)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user?.id) await loadProfile(user.id)
  }, [user?.id, loadProfile])

  const isSuperadmin = profile?.role === 'superadmin'
  const isAdmin =
    profile?.role === 'admin' ||
    profile?.role === 'salon' ||
    profile?.role === 'salon_staff' ||
    isSuperadmin
  const isUser = profile?.role === 'user' || (!profile && !!user)
  const adminSalonIds = profile?.salonIds ?? EMPTY_SALON_IDS
  const staffSalonIds = profile?.staffSalonIds ?? EMPTY_SALON_IDS
  const salonMemberRoles = profile?.salonMemberRoles ?? {}
  const isKlant = profile?.role === 'user' && staffSalonIds.length === 0

  const isFullSalonAdmin = useCallback(
    (salonId: string) => isSuperadmin || adminSalonIds.includes(salonId),
    [isSuperadmin, adminSalonIds]
  )

  const isSalonStaffMember = useCallback(
    (salonId: string) => isSuperadmin || staffSalonIds.includes(salonId),
    [isSuperadmin, staffSalonIds]
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      profileLoading,
      login,
      register,
      logout,
      refreshProfile,
      isSuperadmin,
      isAdmin,
      isUser,
      isKlant,
      adminSalonIds,
      staffSalonIds,
      salonMemberRoles,
      isFullSalonAdmin,
      isSalonStaffMember,
    }),
    [
      user,
      profile,
      loading,
      profileLoading,
      login,
      register,
      logout,
      refreshProfile,
      isSuperadmin,
      isAdmin,
      isUser,
      isKlant,
      adminSalonIds,
      staffSalonIds,
      salonMemberRoles,
      isFullSalonAdmin,
      isSalonStaffMember,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth moet binnen AuthProvider gebruikt worden')
  return ctx
}
